import { useMemo } from 'react';

/**
 * Multi-dimensional filtering hook with AND between filter types, OR within each type
 *
 * @param {Array} data - The data array to filter
 * @param {Object} filters - Filter object with arrays for each filter type
 * @param {Array} filters.tls - Team Leader names to filter by
 * @param {Array} filters.gyms - Gym names to filter by
 * @param {Array} filters.managers - Manager names to filter by
 * @param {Array} filters.sources - Source names to filter by (Direct Walk-in, Company IVR, FD Phone)
 * @param {Array} filters.verificationStatuses - Verification statuses to filter by (tl_verified, mgr_verified, both_verified, unverified)
 * @param {Array} filters.performanceLevels - Performance preset levels (high, medium, low)
 * @param {Object} filters.customPerformanceRange - Custom performance range {min, max}
 * @param {Array} filters.fdAvailability - FD availability counts to filter by (0, 1, 2, 3+)
 * @param {Array} filters.commitmentStatuses - Commitment statuses to filter by (fulfilled, pending, partial, none)
 * @returns {Array} Filtered data array
 */
export function useMultiDimensionalFilter(data, filters) {
  return useMemo(() => {
    if (!data || data.length === 0) {
      return data || [];
    }

    // If no filters are active, return all data
    const hasActiveFilters =
      (filters.tls?.length > 0) ||
      (filters.gyms?.length > 0) ||
      (filters.managers?.length > 0) ||
      (filters.sources?.length > 0) ||
      (filters.verificationStatuses?.length > 0) ||
      (filters.performanceLevels?.length > 0) ||
      (filters.customPerformanceRange !== null && filters.customPerformanceRange !== undefined) ||
      (filters.fdAvailability?.length > 0) ||
      (filters.commitmentStatuses?.length > 0);

    if (!hasActiveFilters) {
      return data;
    }

    return data.filter((row) => {
      // Each filter type: if empty, skip (all pass); if has values, row must match at least one (OR)
      const tlMatch = !filters.tls?.length || filters.tls.includes(row.tl_name);
      const gymMatch = !filters.gyms?.length || filters.gyms.includes(row.gym_name);
      const managerMatch = !filters.managers?.length || filters.managers.includes(row.manager_name);

      // Source filter
      const sourceMatch = !filters.sources?.length || filters.sources.includes(row.source_name);

      // Verification status filter
      const verificationMatch = !filters.verificationStatuses?.length ||
        filters.verificationStatuses.some(status => {
          if (status === 'tl_verified') return row.tl_verified && !row.mgr_verified;
          if (status === 'mgr_verified') return row.mgr_verified && !row.tl_verified;
          if (status === 'both_verified') return row.tl_verified && row.mgr_verified;
          if (status === 'unverified') return !row.tl_verified && !row.mgr_verified;
          return false;
        });

      // Performance filter (connect rate)
      let performanceMatch = true;
      const hasPerformanceFilter = filters.performanceLevels?.length > 0 ||
        (filters.customPerformanceRange !== null && filters.customPerformanceRange !== undefined);

      if (hasPerformanceFilter) {
        const rate = row.connect_rate || 0;

        // Check presets
        const matchesPreset = !filters.performanceLevels?.length ||
          filters.performanceLevels.some(level => {
            if (level === 'high') return rate > 70;
            if (level === 'medium') return rate >= 40 && rate <= 70;
            if (level === 'low') return rate < 40;
            return false;
          });

        // Check custom range
        const matchesCustomRange = filters.customPerformanceRange ?
          (rate >= filters.customPerformanceRange.min && rate <= filters.customPerformanceRange.max) :
          false;

        // Match if either preset OR custom range matches
        performanceMatch = matchesPreset || matchesCustomRange;
      }

      // FD Availability filter
      const fdMatch = !filters.fdAvailability?.length ||
        filters.fdAvailability.some(fd => {
          const fdAvg = row.fd_avg ?? 0;
          if (fd === '3+') return fdAvg >= 3;
          return String(fdAvg) === fd;
        });

      // Commitment status filter
      const commitmentMatch = !filters.commitmentStatuses?.length ||
        filters.commitmentStatuses.includes(row.commitment_status);

      // All non-empty filters must pass (AND between filter types)
      return tlMatch && gymMatch && managerMatch && sourceMatch &&
             verificationMatch && performanceMatch && fdMatch && commitmentMatch;
    });
  }, [data, filters]);
}
